import React, {forwardRef} from 'react';
import {observer} from 'mobx-react';
import MaskedInput from 'react-text-mask';
import TextField from '@mui/material/TextField';
import {InputBaseProps} from '@mui/material';
import {scrollToInput} from '../../helpers/scrollToInput';
import classNames from 'classnames';
import styles from './Input.module.scss';
import {ThemeProvider} from '@mui/material/styles';
import {theme} from '../../helpers/theme';

export interface InputPropsCommon {
    value: string;
    onChange: (value: string) => void;
    onKeyDown: (value: React.SyntheticEvent<EventTarget>) => void;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    error?: string;
    ariaLabel?: string | 'unlabeled';
    onBlur?: () => void;
    type?: 'email' | 'text' | 'tel';
    InputProps?: InputBaseProps;
}

interface InputDateProps extends InputPropsCommon {
    withoutDay?: boolean;
    withoutMonth?: boolean;
    withoutYear?: boolean;
    ref?: React.ForwardedRef<any>;
}

const InputDate: React.FC<InputDateProps> = forwardRef(function InputDate(props) {
    const {value, onChange, onKeyDown, placeholder, disabled, error, ariaLabel, type, onBlur, InputProps, label} =
        props;
    const {withoutDay, withoutMonth, withoutYear} = props;

    const maskFunc = (val: string) => {
        const date = {month: !withoutMonth, day: !withoutDay, year: !withoutYear};

        const pos = [date.month ? 'm' : false, date.day ? 'd' : false, date.year ? 'y' : false].filter((v) => v);

        const dateSplit = val.split('/');

        const [mPos, dPos, yPos] = [pos.indexOf('m'), pos.indexOf('d'), pos.indexOf('y')];

        const monthVal = mPos >= 0 ? dateSplit[mPos] : '';
        const dayVal = dPos >= 0 ? dateSplit[dPos] : '';
        const yearVal = yPos >= 0 ? dateSplit[yPos] : '';

        const dateMask = [];

        if (date.month) {
            !!dateMask.length && dateMask.push('/');
            const firstNum = +monthVal?.[0];
            let secondNum = /\d/;
            if (firstNum === 1) secondNum = /[012]/;
            if (firstNum === 0) secondNum = /[1-9]/;
            dateMask.push(...[/[0-9]/, secondNum]);
        }
        if (date.day) {
            !!dateMask.length && dateMask.push('/');
            const firstNum = +dayVal?.[0];
            let secondNum = /\d/;
            if (firstNum === 3) secondNum = /[01]/;
            if (firstNum === 0) secondNum = /[1-9]/;
            dateMask.push(...[/[0123]/, secondNum]);
        }
        if (date.year) {
            !!dateMask.length && dateMask.push('/');
            if (ariaLabel == 'date-of-birth') {
                const firstNumYear = +yearVal?.[0];
                let secondNum = /\d/;
                let thirdNum = /\d/;
                let fourthNum = /\d/;
                const currentYear = new Date().getFullYear().toString();
                const currentDecade = parseInt(currentYear?.[2]);
                const currentYearLastDigit = parseInt(currentYear?.[3]);
                if (firstNumYear === 1) secondNum = /[9]/;
                if (firstNumYear === 2) {
                    secondNum = /[0]/;
                    thirdNum = new RegExp(`[0-${currentDecade}]`);
                    if (yearVal.length >= 3)
                        fourthNum = +yearVal[2] < currentDecade ? /\d/ : new RegExp(`[0-${currentYearLastDigit}]`);
                }
                dateMask.push(...[/[12]/, secondNum, thirdNum, fourthNum]);
            } else dateMask.push(...[/[12]/, /[8910]/, /\d/, /\d/]);
        }

        return dateMask;
    };

    const pipe = (conformedValue: string) => {
        if (conformedValue?.length && /[2-9]/.test(conformedValue?.[0])) {
            return conformedValue?.replace(conformedValue?.[0], `0${conformedValue?.[0]}`);
        }
        return conformedValue;
    };

    return (
        <div
            className={classNames(styles.Input, styles.inputText, {
                [styles.error]: !!error,
            })}
        >
            <ThemeProvider theme={theme}>
                <MaskedInput
                    value={value}
                    mask={maskFunc}
                    pipe={pipe}
                    guide={false}
                    keepCharPositions
                    onChange={(e) => onChange && onChange(e.target.value)}
                    onKeyDown={(e) => onKeyDown && onKeyDown(e.target.value)}
                    onFocus={(e) => scrollToInput(e.target)}
                    onBlur={onBlur}
                    render={(ref, props) => (
                        <TextField
                            className={styles.root}
                            fullWidth={true}
                            inputProps={{
                                'aria-label': ariaLabel,
                                sx: {fontWeight: 300, height: 'auto'},
                            }}
                            InputLabelProps={{
                                classes: {
                                    filled: styles.filled,
                                    focused: styles.focused,
                                    outlined: styles.outlined,
                                },
                            }}
                            InputProps={{
                                inputRef: ref,
                                type: type,
                                classes: {
                                    notchedOutline: styles.notchedOutline,
                                },
                                ...InputProps,
                            }}
                            FormHelperTextProps={{
                                classes: {
                                    root: styles.errorMessage,
                                },
                            }}
                            value={value}
                            error={!!error}
                            helperText={!!error ? error : ' '}
                            label={label || (placeholder && placeholder)}
                            placeholder={placeholder && placeholder}
                            type={type || 'text'}
                            disabled={disabled}
                            {...props}
                        />
                    )}
                />
            </ThemeProvider>
        </div>
    );
});

export default observer(InputDate);
